import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import GeneralTablesCellsTdWithIconWithTooltip from 'lumen/components/general/tables/cells/td-with-icon-with-tooltip';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import {
  KPI_DISPLAY_CUSTOM_MAPPING,
  KPI_DISPLAY_DEFAULT_MAPPING,
  type KpiDisplay,
  MAPPING_TYPE_MARKET_CODE,
  MAPPING_TYPE_RATE_CODE,
  type MappingType,
} from 'lumen/utils/constants/pms-mapping-constants';

const COLUMN_PROPERTIES = {
  HOTEL_NAME: 'hotelName',
  PMS_NAME: 'pmsName',
  DISPLAY_PREFERENCE: 'displayPreference',
  SEGMENTATION_MAPPING: 'segmentationMapping',
} as const;

interface HotelAndMarketRevenueInsightMultiPmsPortfolioDetailsTableSignature {
  Args: {
    isLoading: boolean;
    model: RevenueInsightSegmentationValue[];
  };
}

export default class HotelAndMarketRevenueInsightMultiPmsPortfolioDetailsTable extends Component<HotelAndMarketRevenueInsightMultiPmsPortfolioDetailsTableSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t(
          'revenue_insight.multi_pms.portfolio_details.hotel_name',
        ),
        valuePath: COLUMN_PROPERTIES.HOTEL_NAME,
        textAlign: 'left' as const,
        hasRightBorder: true,
      },
      {
        name: this.intl.t(
          'revenue_insight.multi_pms.portfolio_details.pms_name',
        ),
        valuePath: COLUMN_PROPERTIES.PMS_NAME,
        displayValuePath: `displayValue.${COLUMN_PROPERTIES.PMS_NAME}`,
        textAlign: 'left' as const,
        tdComponent: GeneralTablesCellsTdWithIconWithTooltip,
      },
      {
        name: this.intl.t(
          'revenue_insight.multi_pms.portfolio_details.kpi_display_preference',
        ),
        valuePath: COLUMN_PROPERTIES.DISPLAY_PREFERENCE,
        textAlign: 'left' as const,
      },
      {
        name: this.intl.t(
          'revenue_insight.multi_pms.portfolio_details.segmentation_mapping',
        ),
        valuePath: COLUMN_PROPERTIES.SEGMENTATION_MAPPING,
        textAlign: 'left' as const,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return this.args.model.map(({ meta }) => ({
      [COLUMN_PROPERTIES.HOTEL_NAME]: meta.name,
      [COLUMN_PROPERTIES.PMS_NAME]: meta.pms_version,
      [COLUMN_PROPERTIES.DISPLAY_PREFERENCE]:
        this.getKpiDisplayPreferenceTranslation(
          meta.kpi_display,
          meta.has_transient_group_split,
        ),
      [COLUMN_PROPERTIES.SEGMENTATION_MAPPING]:
        this.getSegmentationMappingTranslation(meta.segment_breakdown),

      displayValue: {
        [COLUMN_PROPERTIES.PMS_NAME]: {
          tooltipContent: meta.pms_is_realtime
            ? this.intl.t(
                'revenue_insight.multi_pms.portfolio_details.realtime_pms_info',
              )
            : '',
        },
      },
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getKpiDisplayPreferenceTranslation(
    kpiDisplay: KpiDisplay,
    pmsHasTransientGroupSplit: boolean,
  ) {
    switch (kpiDisplay) {
      case KPI_DISPLAY_DEFAULT_MAPPING:
        if (pmsHasTransientGroupSplit) {
          return this.intl.t(
            'revenue_insight.multi_pms.modal_portfolio_details.default_with_transient_group_split',
          );
        }
        return this.intl.t(
          'revenue_insight.multi_pms.modal_portfolio_details.default_without_transient_group_split',
        );

      case KPI_DISPLAY_CUSTOM_MAPPING:
        return this.intl.t(
          'revenue_insight.multi_pms.modal_portfolio_details.custom_mapping',
        );

      default:
        kpiDisplay satisfies never;

        return this.intl.t('general.not_available_short');
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSegmentationMappingTranslation(segmentationMapping: MappingType) {
    switch (segmentationMapping) {
      case MAPPING_TYPE_RATE_CODE:
        return this.intl.t(
          'revenue_insight.multi_pms.modal_portfolio_details.by_rate_code',
        );

      case MAPPING_TYPE_MARKET_CODE:
        return this.intl.t(
          'revenue_insight.multi_pms.modal_portfolio_details.by_market_code',
        );

      default:
        segmentationMapping satisfies never;

        return this.intl.t(
          'revenue_insight.multi_pms.modal_portfolio_details.not_done',
        );
    }
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @isLoading={{@isLoading}}
      @nrOfEmptyRows={{5}}
      @nrOfLoadingRows={{5}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
    />
  </template>
}
